<template>
  <tab-layout :full-width="true">
    <div slot="tabContent">
      <div class="bg-white rounded mb-4 p-3">
        <page-heading>Re-assign nominations</page-heading>

        <div class="draft__info-block mb-3">
          Please make sure the member you're trying to reassign to is linked to the horse and the horse is active.
          <router-link to="/secretary-horses">Click HERE</router-link> to view all horses in the ACA database and link members or make active
        </div>

        <nomination-reassign-tabs :active="type" :draft-id="draftId" />

        <div class="default-form reassign-form">
          <div class="row mb-4">
            <div class="col-6 col-md-3">
              <label><strong>From {{ typeLabel }}</strong></label>
              <app-multiselect
                v-model="form.from"
                :options="candidates"
                :placeholder="'Select ' + typeLabel.toLowerCase()"
                :label="optionLabel"
                track-by="id"
                :internal-search="false"
                :loading="candidatesLoading"
                @search-change="onFromSearch"
              />
            </div>
            <div class="col-6 col-md-3">
              <label><strong>To {{ typeLabel }}</strong></label>
              <app-multiselect
                v-model="form.to"
                :options="toOptions"
                :placeholder="'Select ' + typeLabel.toLowerCase()"
                :label="toOptionLabel"
                track-by="id"
                :internal-search="false"
                :loading="toLoading"
                :disabled="form.from == null"
                @open="onToSearch('')"
                @search-change="onToSearch"
              >
                <template #singleLabel="{ option }">
                  {{ option[toOptionLabel] }}<span v-if="type === 'member' && $isSecretaryOrAdmin && toVolunteerNote(option)" class="text-muted"> ({{ toVolunteerNote(option) }})</span>
                </template>
                <template #option="{ option }">
                  {{ option[toOptionLabel] }}<span v-if="type === 'member' && $isSecretaryOrAdmin && toVolunteerNote(option)" class="text-muted"> ({{ toVolunteerNote(option) }})</span>
                </template>
                <template #noResult>{{ toNoResultText }}</template>
              </app-multiselect>
            </div>
          </div>

          <nomination-event-table
            v-for="event in sourceEvents"
            :key="event.id"
            :event="event"
            :nominations="event.nominations"
            :value="selectedIds"
            @toggle="toggleNomination"
            @reload="reloadSource"
          />

          <div class="mt-3">
            <app-button size="sm" :block="false" :disabled="!canSubmit" :loading="submitting" @click="handleReassign">Reassign</app-button>
          </div>
        </div>
      </div>
    </div>
  </tab-layout>
</template>

<script>
import { defineComponent, reactive, ref, computed, getCurrentInstance, onMounted, watch } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import AppButton from '@/components/AppButton'
import AppMultiselect from '@/components/AppMultiselect'
import NominationReassignTabs from '@/components/Nominations/NominationReassignTabs'
import NominationEventTable from '@/components/Nominations/NominationEventTable'
import useNominationsReassign from '@/composables/nominations/useNominationsReassign'

const TYPE_CONFIG = {
  member: {
    label: 'Competitor',
    optionLabel: 'full_name',
    toOptionLabel: 'full_name',
    toIdKey: 'to_member_id',
    toNoResultText: 'Only members linked to the below horses will show here'
  },
  horse: {
    label: 'Horse',
    optionLabel: 'name',
    toOptionLabel: 'name',
    toIdKey: 'to_horse_id',
    toNoResultText: 'Only horses linked to the below member will show here'
  }
}

export default defineComponent({
  name: 'NominationsReassign',
  components: {
    TabLayout,
    PageHeading,
    AppButton,
    AppMultiselect,
    NominationReassignTabs,
    NominationEventTable
  },
  setup() {
    const { proxy } = getCurrentInstance()
    const draftId = computed(() => proxy.$route.params.draftId)
    const type = computed(() => proxy.$route.params.type)
    const typeLabel = computed(() => TYPE_CONFIG[type.value].label)
    const optionLabel = computed(() => TYPE_CONFIG[type.value].optionLabel)
    const toOptionLabel = computed(() => TYPE_CONFIG[type.value].toOptionLabel)
    const toNoResultText = computed(() => TYPE_CONFIG[type.value].toNoResultText)

    const form = reactive({ from: null, to: null })
    const selectedIds = ref([])
    const submitting = ref(false)

    const {
      candidates,
      candidatesLoading,
      sourceEvents,
      toOptions,
      toLoading,
      fetchCandidates,
      fetchSource,
      searchTo,
      reassign
    } = useNominationsReassign(draftId, type)

    const canSubmit = computed(() => form.from != null && form.to != null && selectedIds.value.length > 0)

    const combPairMap = computed(() => {
      const map = {}
      sourceEvents.value.forEach((event) => {
        (event.nominations || []).forEach((n) => {
          if (n.combNominationId != null) map[n.id] = n.combNominationId
        })
      })
      return map
    })

    function toggleNomination(id, checked) {
      const pairId = combPairMap.value[id]
      const ids = pairId != null ? [id, pairId] : [id]
      if (checked) {
        const toAdd = ids.filter(i => selectedIds.value.indexOf(i) === -1)
        selectedIds.value = selectedIds.value.concat(toAdd)
      } else {
        selectedIds.value = selectedIds.value.filter(i => ids.indexOf(i) === -1)
      }
    }

    function resetForm() {
      form.from = null
      form.to = null
      selectedIds.value = []
      sourceEvents.value = []
      toOptions.value = []
    }

    watch(type, () => {
      resetForm()
      fetchCandidates()
    })

    watch(() => form.from, (val) => {
      form.to = null
      selectedIds.value = []
      toOptions.value = []
      if (val != null) {
        fetchSource(val.id)
      } else {
        sourceEvents.value = []
      }
    })

    function reloadSource() {
      if (form.from != null) fetchSource(form.from.id)
    }

    function onFromSearch(q) {
      fetchCandidates({ q, page: 0 })
    }

    function onToSearch(q) {
      if (form.from == null) return
      searchTo({ q, from_id: form.from.id })
    }

    // Secretary/admin hint: why an otherwise-unfinancial member is a valid target (CAM-1485)
    function toVolunteerNote(option) {
      if (option == null) return ''
      if (option.is_volunteer) return 'Volunteer'
      if (option.is_volunteer_family) return 'Volunteer family'
      return ''
    }

    async function handleReassign() {
      if (!canSubmit.value) return
      submitting.value = true
      const config = TYPE_CONFIG[type.value]
      const payload = {
        nomination_ids: selectedIds.value,
        [config.toIdKey]: form.to.id
      }
      const success = await reassign(payload)
      submitting.value = false
      if (success) {
        resetForm()
        fetchCandidates()
      }
    }

    onMounted(() => {
      fetchCandidates()
    })

    return {
      draftId,
      type,
      typeLabel,
      optionLabel,
      toOptionLabel,
      toNoResultText,
      form,
      selectedIds,
      submitting,
      canSubmit,
      candidates,
      candidatesLoading,
      sourceEvents,
      toOptions,
      toLoading,
      onFromSearch,
      onToSearch,
      toVolunteerNote,
      toggleNomination,
      reloadSource,
      handleReassign
    }
  }
})
</script>



// WEBPACK FOOTER //
// src/views/Nominations/Reassign/NominationsReassign.vue